import * as React from "react";
import { Edit, Box } from "lucide-react";
import { Card } from "../../../ui/components/Card";
import { Callout } from "../../../ui/components/Callout";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import { Text } from "../../../ui/components/Text";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { useI18n } from "../../lib/i18n";
import { useWebSocket } from "../../hooks/useWebSocket";
import { getApiErrorMessage } from "../../lib/api/api-error";
import * as subscriptionsApi from "../../lib/api/subscriptions";
import type { UserSubscriptionGroup } from "../../lib/api/subscriptions";
import { getProductById } from "../../lib/api/products";
import * as s from "../products/ProductPage.css";
import * as emptyStyles from "../products/ProductSubscriptionsModal.css";

export interface UserSubscriptionsSectionProps {
  userId: string;
  token: string | null;
  hideChrome?: boolean;
  refreshKey?: number;
  /** `me` on profile; `named` on user detail admin view. */
  summaryPerspective?: "me" | "named";
  /** First name (or display) used when perspective is `named`. */
  subscriberFirstName?: string;
  onEdit?: (productId: string, productName: string) => void;
}

type UserSubscriptionRow = UserSubscriptionGroup & {
  productName: string;
  summary: string;
};

function buildGroupSummary(
  productName: string,
  eventLabels: string[],
  subscriberLabel: string,
): string {
  if (eventLabels.length === 0) {
    return `Notify ${subscriberLabel} whenever ${productName} changes`;
  }
  if (eventLabels.length === 1) {
    const label = eventLabels[0].toLowerCase();
    if (label === "product alert" || (label.includes("alert") && !label.includes("closed") && !label.includes("opened"))) {
      return `Notify ${subscriberLabel} whenever ${productName} sends an alert`;
    }
    if (label.includes("alert") && label.includes("closed")) {
      return `Notify ${subscriberLabel} whenever ${productName} closes an alert`;
    }
    if (label.includes("alert")) {
      return `Notify ${subscriberLabel} whenever ${productName} opens an alert`;
    }
    if (label.includes("decommission")) {
      return `Notify ${subscriberLabel} whenever ${productName} is decommissioned`;
    }
    if (label.includes("delet")) {
      return `Notify ${subscriberLabel} whenever ${productName} is deleted`;
    }
    if (label.includes("detail")) {
      return `Notify ${subscriberLabel} whenever ${productName} details change`;
    }
    if (label.includes("setting")) {
      return `Notify ${subscriberLabel} whenever ${productName} settings change`;
    }
    return `Notify ${subscriberLabel} whenever ${productName} — ${eventLabels[0]}`;
  }
  return `Notify ${subscriberLabel} whenever ${productName} (${eventLabels.length} notifications)`;
}

export function UserSubscriptionsSection({
  userId,
  token,
  hideChrome = false,
  refreshKey = 0,
  summaryPerspective = "named",
  subscriberFirstName,
  onEdit,
}: UserSubscriptionsSectionProps) {
  const { t } = useI18n();
  const [rows, setRows] = React.useState<UserSubscriptionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [listRefreshKey, setListRefreshKey] = React.useState(0);
  const { subscribe, unsubscribe } = useWebSocket();

  const subscriberLabel =
    summaryPerspective === "me"
      ? "me"
      : subscriberFirstName?.trim() || "user";

  const loadRows = React.useCallback(async () => {
    if (!token) return;
    const groups = await subscriptionsApi.fetchUserSubscriptions(token, userId);
    const withNames = await Promise.all(
      groups.map(async (g) => {
        let productName = `${t("pro.users.subscriptions.productPrefix")}${g.entityId.slice(0, 8)}`;
        try {
          const product = await getProductById(token, g.entityId);
          if (product?.name) productName = product.name;
        } catch {
          // Keep short-id fallback when product is inaccessible/deleted.
        }
        return {
          ...g,
          productName,
          summary: buildGroupSummary(productName, g.eventLabels, subscriberLabel),
        };
      }),
    );
    setRows(withNames);
    setListRefreshKey((k) => k + 1);
  }, [token, userId, t, subscriberLabel]);

  const reload = React.useCallback(async () => {
    try {
      setError(null);
      await loadRows();
    } catch (e) {
      setError(getApiErrorMessage(e, t("pro.users.subscriptions.error.load")));
    }
  }, [loadRows, t]);

  React.useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void loadRows()
      .catch((e) => {
        if (!cancelled) {
          setError(getApiErrorMessage(e, t("pro.users.subscriptions.error.load")));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, userId, t, refreshKey, loadRows]);

  React.useEffect(() => {
    const topic = `entity:subscriptions:user:${userId}`;
    const handler = () => {
      void reload();
    };
    subscribe([topic], handler);
    return () => unsubscribe([topic], handler);
  }, [userId, subscribe, unsubscribe, reload]);

  const fetchList = React.useCallback(
    async (params: ListParams): Promise<ListResult<UserSubscriptionRow>> => {
      const q = (params.search ?? "").trim().toLowerCase();
      const filtered = q
        ? rows.filter(
            (r) =>
              r.productName.toLowerCase().includes(q) ||
              r.entityId.toLowerCase().includes(q) ||
              r.eventLabels.some((label) => label.toLowerCase().includes(q)) ||
              r.summary.toLowerCase().includes(q),
          )
        : rows;
      return {
        data: filtered,
        total: filtered.length,
      };
    },
    [rows],
  );

  const listContextKey = React.useMemo(
    () =>
      `${listRefreshKey}:${rows.map((r) => `${r.entityId}:${r.eventCount}:${r.productName}:${r.summary}`).join(",")}`,
    [listRefreshKey, rows],
  );

  const getContextActions = React.useCallback(
    (row: UserSubscriptionRow): ListViewContextAction[] => {
      if (!onEdit || row.entityType !== "product") return [];
      return [
        {
          id: "edit",
          label: t("pro.users.subscriptions.edit"),
          icon: <Edit size={14} />,
          onClick: () => onEdit(row.entityId, row.productName),
          bulk: false,
        },
      ];
    },
    [onEdit, t],
  );

  const body = (
    <>
      {error ? (
        <div style={{ marginBottom: 12 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      ) : null}

      {loading ? (
        <Text level="body-small">{t("shared.common.loading")}</Text>
      ) : rows.length === 0 ? (
        <div className={emptyStyles.emptyState}>
          <Text level="body-small" className={emptyStyles.emptyMessage}>
            {t("pro.users.subscriptions.noSubscriptions")}
          </Text>
        </div>
      ) : (
        <ListView<UserSubscriptionRow & { id: string }>
          listId={`user-subscriptions-${userId}`}
          columns={[]}
          fetchList={async (params) => {
            const result = await fetchList(params);
            return {
              ...result,
              data: result.data.map((r) => ({ ...r, id: r.entityId })),
            };
          }}
          contextKey={listContextKey}
          queryOptions={{ staleTime: Infinity }}
          enabledViews={["stack"]}
          getContextActions={getContextActions}
          renderStackCard={(row, ctx) => {
            const image = <Box size={ctx.compact ? 28 : 56} color="#9CA3AF" />;

            const eventBadge = {
              id: "events",
              label: `${row.eventCount} ${
                row.eventCount === 1
                  ? t("pro.users.subscriptions.event")
                  : t("pro.users.subscriptions.events")
              }`,
              variant: "default" as const,
            };

            return (
              <ListViewCard
                id={row.entityId}
                layout="stack"
                imageFormat="avatar"
                image={image}
                title={row.productName}
                subtitle={row.summary}
                badges={[eventBadge]}
                compact={ctx.compact}
                onClick={
                  onEdit && row.entityType === "product"
                    ? () => onEdit(row.entityId, row.productName)
                    : undefined
                }
              />
            );
          }}
          compact
          disableLocalPersistence
          defaultPageSize={10}
        />
      )}
    </>
  );

  if (hideChrome) {
    return body;
  }

  return (
    <Card maxWidth={960} className={s.sectionCard}>
      <div className={s.cardInner}>{body}</div>
    </Card>
  );
}
